import { useMemo } from 'react';
import { OrbitControls } from '@react-three/drei';
import type {} from '@react-three/fiber';
import { indexToSquare, parsePlacement } from '@chessmate/game-engine';
import type { Fen, HeatmapMode } from '@chessmate/protocol';
import { Board } from './Board.js';
import { ProjectionReporter } from './BoardProjection.js';
import { computeHighlights, Highlights } from './Highlights.js';
import { CHESS_PLANE } from './projection.js';
import { HeatMap } from './HeatMap.js';
import { PieceMesh } from './PieceMesh.js';
import { fenToPieces, squareToVec3 } from './scene.js';
import { Table } from './Table.js';
import { CoordinateLabels } from './CoordinateLabels.js';
import type { PieceMaterialSpec } from './materials.js';
import type { PieceSizeSpec } from './sizes.js';
import type { PieceTheme, BoardTheme, TableTheme } from './themes.js';

/**
 * Structural view of a lesson step. `DemoStep`/`DrillStep` are assignable to this —
 * board-3d deliberately does NOT import `@chessmate/lessons`.
 */
export interface BoardStepShape {
  readonly fen: string;
  readonly highlight?: { from: string; to: string; check?: boolean };
  readonly free?: boolean;
}

export interface BoardInteraction {
  readonly selected: string | null;
  readonly targets: ReadonlyArray<{ square: string; capture: boolean }>;
}

export interface LessonBoardView {
  readonly fen: Fen | null;
  readonly selected: string | null;
  readonly targets: ReadonlyArray<{ square: string; capture: boolean }>;
  readonly lastMove: { from: string; to: string } | null;
  readonly checkSquare: string | null;
  readonly heatmap: HeatmapMode;
}

/** The king of the side to move, or null if absent. Only the side to move can be in check. */
function checkedKing(fen: string): string | null {
  const placement = parsePlacement(fen);
  const king = fen.split(' ')[1] === 'w' ? 'K' : 'k';
  for (let i = 0; i < 64; i++) {
    if (placement[i] === king) return indexToSquare(i);
  }
  return null;
}

/** Pure projection from a lesson step + live drill interaction into board-3d view props. */
export function lessonBoardProps(
  step: BoardStepShape,
  interaction: BoardInteraction = { selected: null, targets: [] },
): LessonBoardView {
  return {
    fen: step.fen,
    selected: interaction.selected,
    targets: interaction.targets,
    lastMove: step.highlight ? { from: step.highlight.from, to: step.highlight.to } : null,
    checkSquare: step.highlight?.check ? checkedKing(step.fen) : null,
    heatmap: step.free ? 'control' : 'off',
  };
}

export interface LessonBoardProps {
  readonly view: LessonBoardView;
  readonly pieceTheme?: PieceTheme;
  readonly boardTheme?: BoardTheme;
  readonly tableTheme?: TableTheme;
  readonly pieceMaterial?: PieceMaterialSpec;
  readonly pieceSize?: PieceSizeSpec;
  readonly pieceWhiteColor?: string;
  readonly pieceBlackColor?: string;
  readonly showCoords?: boolean;
  /** See `ChessBoardProps.onOverlayTransform`. */
  readonly onOverlayTransform?: (matrix3d: string) => void;
}

export function LessonBoard({
  view,
  pieceTheme,
  boardTheme,
  tableTheme,
  pieceMaterial,
  pieceSize,
  pieceWhiteColor,
  pieceBlackColor,
  showCoords = false,
  onOverlayTransform,
}: LessonBoardProps) {
  const { fen, selected, targets, lastMove, checkSquare, heatmap } = view;
  // Hooks first — the early return below must come after them.
  const pieces = useMemo(() => (fen ? fenToPieces(fen) : []), [fen]);
  const highlights = useMemo(
    () => computeHighlights({ selected, targets, lastMove, checkSquare }),
    [selected, targets, lastMove, checkSquare],
  );
  if (!fen) return null;
  return (
    <>
      {/* Mirrors ChessBoard: same four-light setup and lifted bg so lesson and
          game scenes look identical. */}
      <color attach="background" args={['#1d1b26']} />
      <ambientLight intensity={0.55} />
      <directionalLight position={[6, 7, 5]} intensity={1.0} color="#ffffff" />
      <directionalLight position={[-5, 3, 4]} intensity={0.55} color="#ffe6c2" />
      <directionalLight position={[0, -2, -6]} intensity={0.25} color="#a0b4ff" />
      {/* Scene camera (inert in R3F v9 — the app sets the active one via <Canvas camera>). */}
      <perspectiveCamera position={[0, 9, -9]} fov={45} />
      {/* Rotation locked to match the fixed-orientation DOM tap overlay (SquareOverlay). */}
      <OrbitControls enablePan={false} enableRotate={false} minDistance={6} maxDistance={16} maxPolarAngle={Math.PI / 2.3} />
      {onOverlayTransform && <ProjectionReporter plane={CHESS_PLANE} onChange={onOverlayTransform} />}
      <Table theme={tableTheme} />
      <Board theme={boardTheme} />
      <Highlights highlights={highlights} targets={targets} lastMove={lastMove} />
      {showCoords && <CoordinateLabels />}
      <HeatMap fen={fen} mode={heatmap} />
      {pieces.map((p) => (
        <PieceMesh
          key={`${p.square}-${p.char}`}
          char={p.char}
          position={squareToVec3(p.square)}
          theme={pieceTheme}
          material={pieceMaterial}
          size={pieceSize}
          whiteColor={pieceWhiteColor}
          blackColor={pieceBlackColor}
        />
      ))}
    </>
  );
}
